import { ReactElement, ReactNode } from "react";
import { classNames } from "../utils/classNames";
import { Button, ButtonProps, IconButton } from "./Button";
import { TMessage } from "../i18n/tt";
import { Dialog as HuiDialog } from "@headlessui/react";
import { isFailure, isOk, isPending, Result } from "../common/common";
import { Alert } from "./Alert";
import { paddingMapping } from "./tailwindMappings";
import { Box } from "./Box";
import { IconName } from "./iconMapping";
import { IconProps } from "./Icon";

import { useCallbackHandler, useTranslation } from "../stores/RootStoreContext";
import { DCEvent, EventDispatcher } from "../events/eventsDomain";

function toMaxWidthClassName(maxWidth: Required<DialogProps>["maxWidth"]): string {
  switch (maxWidth) {
    case "xs":
      return "tw-max-w-[30rem]";
    case "sm":
      return "tw-max-w-[40rem]";
    case "md":
      return "tw-max-w-[55rem]";
    case "lg":
      return "tw-max-w-[70rem]";
  }
}

function toHeightClassName(height: DialogProps["height"] = "auto") {
  switch (height) {
    case "sm":
      return "tw-h-[30rem]";
    case "md":
      return "tw-h-[40em]";
    case "lg":
      return "tw-h-[50rem]";
    case "full":
      return "tw-h-full";
  }
}

export type DialogProps = {
  title?: string | TMessage;
  subHeaderElement?: ReactElement;
  onSubmit?: ((dispatch: EventDispatcher) => void) | DCEvent[];
  isSubmitDisabled?: boolean;
  isSubmitting?: boolean;
  submitLabel?: string | TMessage;
  submitIcon?: IconName | IconProps;
  submitColor?: ButtonProps["color"];
  onCancel?: ((dispatch: EventDispatcher) => void) | DCEvent[];
  isCancelDisabled?: boolean;
  cancelLabel?: string | TMessage;
  height?: "auto" | "sm" | "md" | "lg" | "full";
  maxWidth?: "xs" | "sm" | "md" | "lg";
  children?: ReactNode;
  actions?: ReactNode;
  fullscreen?: boolean;
  submitResult?: Result<TMessage, TMessage>;
  padding?: "sm" | "md" | "lg";
  showAlert?: boolean;
  headerElement?: ReactNode;
  alertPosition?: "top" | "bottom";
  isSubDialog?: boolean; // Increase z-index (needed for some nested dialogs)
};

export const Dialog = ({
  title,
  subHeaderElement,
  children,
  maxWidth = "md",
  height = "auto",
  isCancelDisabled,
  cancelLabel,
  submitLabel,
  submitIcon,
  isSubmitting,
  isSubmitDisabled,
  actions,
  fullscreen,
  submitResult,
  padding,
  showAlert,
  headerElement,
  alertPosition,
  ...props
}: DialogProps) => {
  const { t } = useTranslation();
  const submitting = isSubmitting || isPending(submitResult);
  const displayAlert = showAlert ?? true;
  const onSubmit = useCallbackHandler(props.onSubmit);
  const onCancel = useCallbackHandler(props.onCancel);

  const renderAlert = () => {
    if (displayAlert && isOk(submitResult)) {
      return <Alert type={"success"} message={<>{t(submitResult.data)}</>} />;
    }
    if (displayAlert && isFailure(submitResult)) {
      return <Alert type={"error"} message={<>{t(submitResult.error)}</>} />;
    }
    return null;
  };
  return (
    <HuiDialog
      open
      onClose={() => {
        if (!isCancelDisabled && !submitting) {
          onCancel?.();
        }
      }}
      className={classNames(
        "tw-relative",
        "tw-inset-0",
        "tw-w-screen",
        "tw-h-screen",
        props.isSubDialog ? "tw-z-[1400]" : "tw-z-[1200]",
      )}
    >
      <div className={"tw-fixed tw-inset-0 backdrop"}></div>
      <div className={"tw-fixed tw-inset-0 tw-flex tw-justify-center tw-items-center tw-p-4"}>
        <HuiDialog.Panel
          className={classNames(
            "tw-flex-1",
            "tw-flex",
            "tw-flex-col",
            "tw-max-h-full",
            "tw-rounded",
            "bg-default",
            "tw-shadow-2xl",
            "tw-overflow-hidden",
            padding && paddingMapping[padding],
            !fullscreen && toMaxWidthClassName(maxWidth),
            !fullscreen && toHeightClassName(height),
            fullscreen && ["tw-absolute", "tw-inset-2", "sm:tw-inset-10"],
          )}
        >
          {(title || onCancel) && (
            <div className={"tw-flex tw-items-center tw-gap-4 tw-px-4 tw-pt-2"}>
              {title && (
                <HuiDialog.Title as="h4" className={"tw-my-2"}>
                  {typeof title === "string" ? title : t(title)}
                </HuiDialog.Title>
              )}
              <Box className={"tw-flex-1"}>{headerElement}</Box>
              {onCancel && !isCancelDisabled && (
                <IconButton
                  aria-hidden
                  tabIndex={-1}
                  icon={{ name: "x", weight: "bold" }}
                  tooltip={t("close")}
                  color={"inherit"}
                  onClick={onCancel}
                  disabled={isCancelDisabled || submitting}
                />
              )}
            </div>
          )}
          {subHeaderElement}
          <form
            onSubmit={(event) => {
              event.preventDefault();
              onSubmit?.();
            }}
            className={classNames(
              "tw-flex tw-flex-col tw-flex-1 tw-overflow-y-auto tw-pb-6 tw-p-4",
            )}
          >
            {alertPosition !== "bottom" && renderAlert()}
            {children}
          </form>
          {actions ? (
            <div className={"tw-px-4 tw-pb-4"}>{actions}</div>
          ) : (
            <div
              className={
                "tw-px-4 tw-pb-4 tw-flex tw-flex-col tw-gap-2 tw-justify-end md:tw-flex-row-reverse md:tw-justify-start"
              }
            >
              {onSubmit && !isOk(submitResult) && (
                <Button
                  data-testid={"dialogButton"}
                  className={"tw-min-w-[10rem]"}
                  variant={"primary"}
                  icon={submitIcon}
                  label={submitLabel ?? { key: "submit" }}
                  loading={submitting}
                  onClick={onSubmit}
                  disabled={isSubmitDisabled}
                  color={props.submitColor}
                />
              )}
              {onCancel && (
                <Button
                  className={"tw-min-w-[10rem]"}
                  variant={"secondary"}
                  label={cancelLabel ?? (isOk(submitResult) ? t("close") : t("cancel"))}
                  onClick={onCancel}
                  disabled={isCancelDisabled || submitting}
                />
              )}
              {alertPosition === "bottom" && renderAlert()}
            </div>
          )}
        </HuiDialog.Panel>
      </div>
    </HuiDialog>
  );
};
